/**
 * Shared access to the Web Audio context.
 *
 * Safari only exposes the constructor under its prefixed name, which the DOM
 * lib does not declare. Declaring it here keeps the lookup type-safe rather
 * than casting `window` at each call site.
 */
declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

/**
 * A new `AudioContext`, or null where audio is unavailable: an autoplay policy
 * that refuses construction, a browser with neither constructor, or a
 * locked-down embed. Callers treat audio as a courtesy and carry on without it.
 */
export const createAudioContext = (): AudioContext | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const Ctor = window.AudioContext ?? window.webkitAudioContext;
  if (Ctor === undefined) {
    return null;
  }
  try {
    return new Ctor();
  } catch {
    return null;
  }
};
